import { fetchProductsStatistics } from '../../api';
import { calculateProductsChartOptions } from './products-chart-options';
import { chartConfig } from '@/features/order-statistics/config';
import { Env } from '@/shared/config/environment';

const state = {
  productsStatistics: null,
  isLoadingProducts: false,
  productsError: null,
  activeFilters: {
    product: null,
    partner: null,
    period: [],
    periodMode: null,
  },

  productsAmountChartOptions: null,
  productsEarningsChartOptions: null,
  productsCountChartOptions: null,
};

const mutations = {
  SET_PRODUCTS_STATISTICS(state, stats) {
    state.productsStatistics = stats;
  },
  SET_LOADING_PRODUCTS(state, isLoading) {
    state.isLoadingProducts = isLoading;
  },
  SET_PRODUCTS_ERROR(state, error) {
    state.productsError = error;
  },
  SET_PRODUCTS_AMOUNT_CHART_OPTIONS(state, options) {
    state.productsAmountChartOptions = options;
  },
  SET_PRODUCTS_EARNINGS_CHART_OPTIONS(state, options) {
    state.productsEarningsChartOptions = options;
  },
  SET_PRODUCTS_COUNT_CHART_OPTIONS(state, options) {
    state.productsCountChartOptions = options;
  },
  SET_ACTIVE_FILTERS(state, filter) {
    state.activeFilters = filter;
  },
  SET_ACTIVE_FILTERS_PERIOD_MODE(state, mode) {
    state.activeFilters.periodMode = mode;
  },
};

const actions = {
  clearProductsData({ commit }) {
    commit('SET_PRODUCTS_STATISTICS', null);
    commit('SET_PRODUCTS_AMOUNT_CHART_OPTIONS', null);
    commit('SET_PRODUCTS_COUNT_CHART_OPTIONS', null);
    commit('SET_PRODUCTS_EARNINGS_CHART_OPTIONS', null);
  },
  async fetchProductsStatistics({ commit, dispatch, state }) {
    commit('SET_LOADING_PRODUCTS', true);
    commit('SET_PRODUCTS_ERROR', null);
    dispatch('clearProductsData');

    try {
      const stats = await fetchProductsStatistics(state.activeFilters);
      commit('SET_PRODUCTS_STATISTICS', stats);
      dispatch('calculateProductsChartOptions');

      return stats;
    } catch (error) {
      const errorMessage = error.response?.data?.message
        || error.response?.data?.error
        || error.message
        || 'Не вдалося отримати статистику продажів';

      commit('SET_PRODUCTS_ERROR', errorMessage);
      if (Env.features.showNetworkErrorMessages) {
        dispatch('notificationModule/showNotificationErrorMessage', {
          error: error,
          title: 'Не вдалося отримати статистику продажів',
          message: errorMessage,
        }, { root: true });
      }

      throw error;
    } finally {
      commit('SET_LOADING_PRODUCTS', false);
    }
  },
  calculateProductsChartOptions: ({ state, commit }) => {
    if (state.productsStatistics) {
      const centerText = '100%';

      const chartAmountOptions = calculateProductsChartOptions(state.productsStatistics, 'total_amount', centerText);
      commit('SET_PRODUCTS_AMOUNT_CHART_OPTIONS', chartAmountOptions);

      const chartEarningsOptions = calculateProductsChartOptions(state.productsStatistics, 'total_earnings', centerText);
      commit('SET_PRODUCTS_EARNINGS_CHART_OPTIONS', chartEarningsOptions);

      const chartCountOptions = calculateProductsChartOptions(state.productsStatistics, 'order_count', centerText);
      commit('SET_PRODUCTS_COUNT_CHART_OPTIONS', chartCountOptions);
    }
  },
  setupFilter: ({ state, commit, dispatch }, { filters, mode = 'replace', refetchData = true }) => {
    if (mode === 'replace') {
      commit('SET_ACTIVE_FILTERS', filters);
    } else {
      commit('SET_ACTIVE_FILTERS', { ...state.activeFilters, ...filters });
    }

    if (refetchData) {
      dispatch('fetchProductsStatistics');
    }
  },
  setupPeriodMode: ({ commit }, mode) => {
    commit('SET_ACTIVE_FILTERS_PERIOD_MODE', mode);
  },
};

const getters = {
  productsStatistics: (state) => state.productsStatistics,
  isLoadingProducts: (state) => state.isLoadingProducts,
  productsError: (state) => state.productsError,

  isProductsDataReady: (state) => { return state.isLoadingProducts === false && state.productsStatistics !== null &&
    state.productsAmountChartOptions !== null && state.productsEarningsChartOptions !== null
    && state.productsCountChartOptions !== null; },
  isChartEmpty: (state) => !state.productsStatistics ||
    state.productsStatistics.reduce((total, product) => total + (product?.statistics?.order_count || 0), 0) === 0,

  getStatisticChartOptions: (state) => (type) => { return state[`products${type.charAt(0).toUpperCase() + type.slice(1)}ChartOptions`]; },
  getEmptyProductsChartMessage: () => chartConfig.emptyMessages['message-4'].title,
  getActiveFilters: (state) => state.activeFilters,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
